import React, { useState, useEffect, useTransition } from 'react';
import { Users, Trophy, Award, Calendar, ActivitySquare, Medal, PieChart, BarChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getParticipants } from '../services/participantService';
import { getCompetitions } from '../services/competitionService';
import { getResults } from '../services/resultService';
import { getHouses } from '../services/houseService';
import { getActiveSportsYear } from '../services/sportsYearService';
import { Competition, House } from '../types/models';
import { supabase } from '../lib/supabase';

interface HousePerformance {
  house_id: string;
  house_name: string;
  house_color: string;
  competition_year: number;
  total_points: number;
  participating_students: number;
  total_participations: number;
  first_place_count: number;
  second_place_count: number;
  third_place_count: number;
}

interface Champion {
  champion_id: string;
  student_id: string;
  competition_year: number;
  gender: string;
  total_points: number;
  categories_won: number;
  student?: {
    first_name: string;
    last_name: string;
    first_name_tamil?: string;
    last_name_tamil?: string;
    house?: {
      house_id: string;
      house_name: string;
      house_color: string;
    };
  };
}

interface CompetitionStats {
  notStarted: number;
  inProgress: number;
  completed: number;
  total: number;
}

const Dashboard: React.FC = () => {
  const [participantsCount, setParticipantsCount] = useState(0);
  const [competitionsCount, setCompetitionsCount] = useState(0);
  const [resultsCount, setResultsCount] = useState(0);
  const [upcomingCompetitions, setUpcomingCompetitions] = useState<Competition[]>([]);
  const [housePerformance, setHousePerformance] = useState<HousePerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();
  const [competitionStats, setCompetitionStats] = useState<CompetitionStats>({
    notStarted: 0,
    inProgress: 0,
    completed: 0,
    total: 0
  });
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const { t } = useTranslation('common');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const year = await getActiveSportsYear();
        setActiveYear(year);

        // Fetch all required data
        const [participants, competitions, results, houses] = await Promise.all([
          getParticipants(),
          getCompetitions(),
          getResults(),
          getHouses()
        ]);

        const competitionsForYear = competitions.filter(c => c.competition_year === year);
        const resultsForYear = results.filter((r: any) => r.competition_year === year);

        // Use startTransition for state updates that might cause suspension
        startTransition(() => {
          setParticipantsCount(participants.length);
          setCompetitionsCount(competitionsForYear.length);
          setResultsCount(resultsForYear.length);

          // Find upcoming competitions (not completed) within the active year
          const upcoming = competitionsForYear
            .filter(comp => comp.status !== 'completed')
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .slice(0, 5);

          setUpcomingCompetitions(upcoming);

          // Calculate competition statistics for the active year
          const stats = {
            notStarted: competitionsForYear.filter(comp => comp.status === 'not_started').length,
            inProgress: competitionsForYear.filter(comp => comp.status === 'in_progress').length,
            completed: competitionsForYear.filter(comp => comp.status === 'completed').length,
            total: competitionsForYear.length
          };

          setCompetitionStats(stats);
        });

        // Fetch house performance data for the active year only
        const { data: housePerformanceData, error: housePerformanceError } = await supabase
          .from('house_performance')
          .select('*')
          .eq('competition_year', year)
          .order('total_points', { ascending: false });

        if (housePerformanceError) {
          console.error('Error fetching house performance:', housePerformanceError);
          throw housePerformanceError;
        }

        // Fetch individual results for the active year
        const { data: individualResults, error: individualError } = await supabase
          .from('competition_results')
          .select(`
            points_earned,
            student:students(
              house_id
            )
          `)
          .eq('competition_year', year);

        if (individualError) {
          console.error('Error fetching individual results:', individualError);
          throw individualError;
        }

        // Fetch team results for the active year
        const { data: teamResults, error: teamError } = await supabase
          .from('team_results')
          .select('house_id, points_earned')
          .eq('competition_year', year);

        if (teamError) {
          console.error('Error fetching team results:', teamError);
          throw teamError;
        }

        // Calculate individual points per house
        const individualPointsByHouse: Record<string, number> = {};
        individualResults?.forEach(result => {
          if (result.student?.house_id) {
            const houseId = result.student.house_id;
            individualPointsByHouse[houseId] = (individualPointsByHouse[houseId] || 0) + result.points_earned;
          }
        });

        // Calculate team points per house
        const teamPointsByHouse: Record<string, number> = {};
        teamResults?.forEach(result => {
          const houseId = result.house_id;
          teamPointsByHouse[houseId] = (teamPointsByHouse[houseId] || 0) + result.points_earned;
        });

        // Count students per house
        const studentsPerHouse: Record<string, number> = {};
        participants.forEach(student => {
          if (student.house_id) {
            studentsPerHouse[student.house_id] = (studentsPerHouse[student.house_id] || 0) + 1;
          }
        });

        // Combine data
        const enhancedHousePerformance = (housePerformanceData || []).map(house => {
          return {
            ...house,
            individual_points: individualPointsByHouse[house.house_id] || 0,
            team_points: teamPointsByHouse[house.house_id] || 0,
            student_count: studentsPerHouse[house.house_id] || 0
          };
        });

        setHousePerformance(enhancedHousePerformance);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const statsCards = [
    {
      title: t('dashboard.stats.students'),
      value: participantsCount,
      icon: <Users className="h-8 w-8 text-blue-500" />,
      link: '/participants',
      color: 'bg-blue-50 border-blue-200'
    },
    {
      title: t('dashboard.stats.events'),
      value: competitionsCount,
      icon: <ActivitySquare className="h-8 w-8 text-green-500" />,
      link: '/competitions',
      color: 'bg-green-50 border-green-200'
    },
    {
      title: t('dashboard.stats.resultsRecorded'),
      value: resultsCount,
      icon: <Award className="h-8 w-8 text-amber-500" />,
      link: '/results',
      color: 'bg-amber-50 border-amber-200'
    }
  ];

  // Calculate percentages for the competition status chart
  const getStatusPercentage = (status: keyof CompetitionStats): number => {
    if (competitionStats.total === 0) return 0;
    return Math.round((competitionStats[status] / competitionStats.total) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-8 rounded-xl shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">{t('dashboard.title')}</h1>
          {activeYear !== null && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
              Sportsår: {activeYear}
            </span>
          )}
        </div>
        
        {(loading || isPending) ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {statsCards.map((card, index) => (
                <Link 
                  key={index} 
                  to={card.link}
                  className={`${card.color} border rounded-lg p-6 transition-transform duration-200 hover:shadow-md hover:-translate-y-1`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-gray-600 font-medium">{card.title}</p>
                      <p className="text-3xl font-bold text-gray-800 mt-2">{card.value}</p>
                    </div>
                    <div className="bg-white p-3 rounded-full shadow-sm">
                      {card.icon}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Competition Status Statistics */}
            <div className="mt-8 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="px-4 py-5 border-b border-gray-200">
                <div className="flex items-center">
                  <PieChart className="h-5 w-5 text-blue-500 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Konkurransar Status
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-gray-400 mr-2"></div>
                        <span className="text-sm font-medium text-gray-700">
                          {t('competitions.status.not_started')}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-800">
                        {competitionStats.notStarted}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-gray-400 h-2.5 rounded-full" 
                        style={{ width: `${getStatusPercentage('notStarted')}%` }}
                      ></div>
                    </div>
                    <div className="text-right mt-1 text-xs text-gray-500">
                      {getStatusPercentage('notStarted')}%
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                        <span className="text-sm font-medium text-gray-700">
                          {t('competitions.status.in_progress')}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-800">
                        {competitionStats.inProgress}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-500 h-2.5 rounded-full" 
                        style={{ width: `${getStatusPercentage('inProgress')}%` }}
                      ></div>
                    </div>
                    <div className="text-right mt-1 text-xs text-gray-500">
                      {getStatusPercentage('inProgress')}%
                    </div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span className="text-sm font-medium text-gray-700">
                          {t('competitions.status.completed')}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-800">
                        {competitionStats.completed}
                      </span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2.5">
                      <div 
                        className="bg-green-500 h-2.5 rounded-full" 
                        style={{ width: `${getStatusPercentage('completed')}%` }}
                      ></div>
                    </div>
                    <div className="text-right mt-1 text-xs text-gray-500">
                      {getStatusPercentage('completed')}%
                    </div>
                  </div>
                </div>
                
                {/* Overall progress bar */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Total: {competitionStats.total} konkurransar
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {getStatusPercentage('completed')}% fullført
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div className="flex h-full">
                      <div 
                        className="bg-gray-400 h-full" 
                        style={{ width: `${getStatusPercentage('notStarted')}%` }}
                      ></div>
                      <div 
                        className="bg-blue-500 h-full" 
                        style={{ width: `${getStatusPercentage('inProgress')}%` }}
                      ></div>
                      <div 
                        className="bg-green-500 h-full" 
                        style={{ width: `${getStatusPercentage('completed')}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>Ikkje starta: {competitionStats.notStarted}</span>
                    <span>Pågår: {competitionStats.inProgress}</span>
                    <span>Fullført: {competitionStats.completed}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* House Performance */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <div className="px-4 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Trophy className="h-5 w-5 text-amber-500 mr-2" />
                      <h2 className="text-lg font-semibold text-gray-800">
                        {t('dashboard.housePerformance.title')}
                      </h2>
                    </div>
                    <Link 
                      to="/houses/performance" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      {t('dashboard.housePerformance.viewFullscreen')}
                    </Link>
                  </div>
                </div>
                <div className="p-4">
                  {housePerformance.map((house, index) => (
                    <div 
                      key={house.house_id}
                      className={`flex flex-col p-4 ${
                        index !== housePerformance.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: house.house_color }}
                          />
                          <p className="font-medium text-gray-900 text-lg">{house.house_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">{house.total_points}</p>
                          <p className="text-sm text-gray-500">{t('dashboard.housePerformance.points')}</p>
                        </div>
                      </div>
                      
                      <div className="relative h-2 bg-gray-200 rounded-full mb-3">
                        <div 
                          className="absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out"
                          style={{ 
                            width: `${Math.min(100, (house.total_points / (housePerformance[0]?.total_points || 1)) * 100)}%`,
                            backgroundColor: house.house_color 
                          }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-blue-800 font-medium">{t('dashboard.housePerformance.individual')}</p>
                          <p className="text-blue-900">{house.individual_points} {t('dashboard.housePerformance.pts')}</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <p className="text-green-800 font-medium">{t('dashboard.housePerformance.team')}</p>
                          <p className="text-green-900">{house.team_points} {t('dashboard.housePerformance.pts')}</p>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="text-gray-800 font-medium">{t('dashboard.housePerformance.students')}</p>
                          <p className="text-gray-900">{house.student_count}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
                        <div className="flex items-center">
                          <Medal className="h-4 w-4 mr-1 text-yellow-500" />
                          <span>{house.first_place_count}</span>
                        </div>
                        <div className="flex items-center">
                          <Medal className="h-4 w-4 mr-1 text-gray-400" />
                          <span>{house.second_place_count}</span>
                        </div>
                        <div className="flex items-center">
                          <Medal className="h-4 w-4 mr-1 text-amber-700" />
                          <span>{house.third_place_count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <div className="px-4 py-5 border-b border-gray-200">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">
                      {t('dashboard.upcomingEvents.title')}
                    </h2>
                  </div>
                </div>
                
                {upcomingCompetitions.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {upcomingCompetitions.map((competition) => (
                      <Link
                        key={competition.event_id}
                        to={`/competitions/${competition.event_id}`}
                        className="block p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{competition.event_name}</p>
                            <p className="text-sm text-gray-500">{competition.category?.category_name}</p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {competition.status === 'not_started' 
                              ? t('dashboard.upcomingEvents.status.notStarted')
                              : t('dashboard.upcomingEvents.status.inProgress')
                            }
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <Trophy className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <h3 className="text-gray-600 font-medium">
                      {t('dashboard.upcomingEvents.noEvents.title')}
                    </h3>
                    <p className="text-gray-500 mt-1">
                      {t('dashboard.upcomingEvents.noEvents.description')}
                    </p>
                    <Link 
                      to="/competitions/new" 
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {t('dashboard.upcomingEvents.noEvents.action')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;